/**
 * Group Move Handler
 * Handles complex group-move operations with date shifting for descendant nodes
 *
 * Note: This module processes dynamic WebSocket messages whose shapes are not
 * statically known, so `any` is used intentionally for message/result payloads.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { log } from '@/shared/utils/logger';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/MultiProjectContext';
import type { ProjectData, ProjectNode } from '@/shared/types/projectData';
import {
  formatDateToLocalISODate,
  isDateOnlyValue,
  parseDateValuePreservingLocalDay,
} from '@/shared/utils/dates';
import { setGanttOrder } from '@/shared/utils/viewOrdering';
import { buildCanonicalRootNodeIds } from '@/shared/utils/projectDataUtils';
import {
  isGanttGroupMoveImmutableAnchor,
  isGanttGroupMoveMovableNode,
} from '@/shared/utils/ganttGroupMovePolicy';

interface GroupMoveHandlerParams {
  message: Record<string, any>;
  currentData: ProjectData;
  operationProjectId: string;
  isGroupMove: boolean;
}

function applySiblingOrder(nodes: ProjectNode[], parentId: string | null, nodeIds: string[]): void {
  const orderMap = new Map<string, number>();
  nodeIds.forEach((id, idx) => orderMap.set(id, idx));

  const siblings = nodes.filter(node => (node.parentId || null) === parentId);
  const listed = siblings.filter(node => orderMap.has(node.id));
  const unlisted = siblings.filter(node => !orderMap.has(node.id));

  listed.sort((left, right) => (orderMap.get(left.id)! - orderMap.get(right.id)!));
  const ordered = [...listed, ...unlisted];

  ordered.forEach((node, index) => {
    node.order = index;
    setGanttOrder(node, index);
  });
}

/**
 * Process group-move operation with smart date shifting for descendants
 */
export function handleGroupMove({
  message,
  currentData,
  operationProjectId,
  isGroupMove
}: GroupMoveHandlerParams): ProjectData {
  const updatedNodesFull = (message.result as any)?.updatedNodesFull;
  const hasUpdatedNodes = Array.isArray(updatedNodesFull) && updatedNodesFull.length > 0;

  if (!isGroupMove || !hasUpdatedNodes) {
    return currentData;
  }

  log.debug('WebSocket', '🔍 [GROUP-MOVE] Processing group-move inside setState');
  const updatedNodesArr = updatedNodesFull as any[];

  // Log sample updated node dates for verification
  const sampleNode = updatedNodesArr[0];
  if (sampleNode) {
    log.debug('WebSocket', '🔍 [GROUP-MOVE] Sample updated node from server:', {
      id: sampleNode.id,
      startDate: sampleNode.startDate,
      endDate: sampleNode.endDate,
      date: sampleNode.date
    });
  }

  log.debug('WebSocket', '🔍 [GROUP-MOVE] operationProjectId:', operationProjectId);

  // CRITICAL: Filter currentData.nodes to only include nodes from THIS project
  // In multi-project mode, currentData.nodes may contain nodes from multiple projects
  const currentProjectNodes = currentData.nodes.filter((n: any) => {
    if (!operationProjectId) return true; // If no project ID, include all
    const nodeProjectId = typeof n.id === 'string' && n.id.includes(':')
      ? getProjectIdFromNodeId(n.id)
      : operationProjectId;
    return nodeProjectId === operationProjectId;
  });
  log.debug('WebSocket', '🔍 [GROUP-MOVE] Filtered to currentProjectNodes:', currentProjectNodes.length);

  // Normalize IDs for updatedNodes to match current data shape
  const idsAreNamespaced = Array.isArray(currentProjectNodes) &&
    currentProjectNodes.some(n => typeof n.id === 'string' && n.id.includes(':'));

  const normalizeId = (id: string) => {
    if (idsAreNamespaced) {
      if (!id.includes(':') && operationProjectId) return `${operationProjectId}:${id}`;
      return id;
    }
    return getNodeId(id);
  };

  const normalizedUpdated = updatedNodesArr.map((n: any) => {
    const normalizedId = normalizeId(String(n.id || ''));
    const normalizedParentId = n.parentId ? normalizeId(String(n.parentId)) : n.parentId;
    const normalizedDeps = Array.isArray(n.dependencies)
      ? n.dependencies.map((d: any) => normalizeId(String(d)))
      : n.dependencies;
    return { ...n, id: normalizedId, parentId: normalizedParentId, dependencies: normalizedDeps };
  });
  const updatedMap = new Map<string, any>(normalizedUpdated.map((n: any) => [n.id, n]));

  // Extract group-move metadata for date shifting
  const daysDelta = (message.result as any)?.daysDelta;
  const movedGroupId = (message.result as any)?.nodeId;
  const targetParentId = ((message.result as any)?.parentId ?? null) as string | null;
  const targetNodeIds = Array.isArray((message.result as any)?.nodeIds)
    ? (message.result as any).nodeIds.map((id: any) => normalizeId(String(id)))
    : [];
  const sourceParentId = (message.result as any)?.sourceParentId
    ? normalizeId(String((message.result as any).sourceParentId))
    : null;
  const sourceNodeIds = Array.isArray((message.result as any)?.sourceNodeIds)
    ? (message.result as any).sourceNodeIds.map((id: any) => normalizeId(String(id)))
    : [];

  const isDescendant = (nodeParentId?: string | null): boolean => {
    if (!nodeParentId) return false;
    const cleanParentId = getNodeId(nodeParentId);
    const cleanGroupId = movedGroupId ? getNodeId(movedGroupId) : null;

    if (cleanParentId === cleanGroupId) return true;
    const parentNode = currentProjectNodes.find((pn: any) => getNodeId(pn.id) === cleanParentId);
    return parentNode ? isDescendant(parentNode.parentId) : false;
  };

  const projectNodeMap = new Map<string, ProjectNode>(currentProjectNodes.map((node: any) => [node.id, node]));

  currentProjectNodes.forEach((node: any) => {
    const serverNode = updatedMap.get(node.id);

    if (!serverNode) {
      if (daysDelta && node.parentId && isDescendant(node.parentId) && isGanttGroupMoveMovableNode(node)) {
        projectNodeMap.set(node.id, applyDateShift(node, daysDelta));
      }
      return;
    }

    const merged = { ...node, ...serverNode } as any;
    if (isGanttGroupMoveImmutableAnchor(node)) {
      merged.startDate = node.startDate;
      merged.endDate = node.endDate;
      merged.dueDate = node.dueDate;
      merged.date = node.date;
    } else {
      if (node.startDate && !serverNode.startDate) merged.startDate = node.startDate;
      if (node.endDate && !serverNode.endDate) merged.endDate = node.endDate;
      if (node.dueDate && !serverNode.dueDate) merged.dueDate = node.dueDate;
      if (node.date && !serverNode.date) merged.date = node.date;
    }
    projectNodeMap.set(node.id, merged);
  });

  normalizedUpdated.forEach((serverNode: any) => {
    if (!projectNodeMap.has(serverNode.id)) {
      projectNodeMap.set(serverNode.id, serverNode as ProjectNode);
    }
  });

  const updatedProjectNodes = Array.from(projectNodeMap.values());

  if (targetNodeIds.length > 0) {
    applySiblingOrder(updatedProjectNodes, targetParentId, targetNodeIds);
  }

  const shouldApplySourceOrder = sourceNodeIds.length > 0 && sourceParentId !== targetParentId;
  if (shouldApplySourceOrder) {
    applySiblingOrder(updatedProjectNodes, sourceParentId, sourceNodeIds);
  }

  // Preserve nodes from other projects
  const otherProjectNodes = currentData.nodes.filter((n: any) => {
    if (!operationProjectId) return false;
    const nodeProjectId = typeof n.id === 'string' && n.id.includes(':')
      ? getProjectIdFromNodeId(n.id)
      : operationProjectId;
    return nodeProjectId !== operationProjectId;
  });

  const finalNodes = [...updatedProjectNodes, ...otherProjectNodes];

  log.debug('WebSocket', '🔍 [GROUP-MOVE] Final result:', {
    updatedCount: updatedProjectNodes.length,
    otherProjectsCount: otherProjectNodes.length,
    totalCount: finalNodes.length
  });

  return {
    ...currentData,
    nodes: finalNodes,
    rootNodeIds: buildCanonicalRootNodeIds(finalNodes),
  };
}

/**
 * Apply date shift to a node
 */
function shiftStoredDateValueByDays(dateValue: string, daysDelta: number): string {
  const shiftedDate = parseDateValuePreservingLocalDay(dateValue);
  shiftedDate.setDate(shiftedDate.getDate() + daysDelta);
  return isDateOnlyValue(dateValue)
    ? formatDateToLocalISODate(shiftedDate)
    : shiftedDate.toISOString();
}

function applyDateShift(node: any, daysDelta: number): any {
  const shiftedNode = { ...node };

  if (typeof node.startDate === 'string' && node.startDate) {
    shiftedNode.startDate = shiftStoredDateValueByDays(node.startDate, daysDelta);
  }
  if (typeof node.endDate === 'string' && node.endDate) {
    shiftedNode.endDate = shiftStoredDateValueByDays(node.endDate, daysDelta);
  }
  if (typeof node.dueDate === 'string' && node.dueDate) {
    shiftedNode.dueDate = shiftStoredDateValueByDays(node.dueDate, daysDelta);
  }
  if (typeof node.date === 'string' && node.date) {
    shiftedNode.date = shiftStoredDateValueByDays(node.date, daysDelta);
  }

  return shiftedNode;
}
